import { BasicColumnTypes, SemanticTypes } from '@opengov/ogql-types';
import { FilterOperators, IColumnFilter, IColumnFilterBooleanOperator, ISegmentFilter } from '@opengov/reporting-types';
import {
  getColumnByNameFromList,
  getColumnName,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';

import { IReportLocale } from '../locale';

const formatDateValue = (value, dateFormat: IReportLocale['reportTable']['dateFormat']): string => {
  const dateTime = DateTime.fromSQL(value);
  return dateTime.isValid ? dateFormat({ date: dateTime.toJSDate() }) : value;
};

const formatNumericValue = (value, numberFormat: IReportLocale['reportTable']['numberFormat']): string => {
  const formattedNumber = numberFormat({ value: parseFloat(value) });
  const isTruncated =
    typeof value === 'string' && parseFloat(value).toString().indexOf('.') + 4 < parseFloat(value).toString().length;

  return isTruncated ? `${formattedNumber}...` : formattedNumber;
};

const formatBooleanValue = (value, reportText: IReportLocale): string =>
  value === null || value === '' ? reportText.reportExplorer.emptyValues : capitalize(value);

// Formatters below only run once isValueTruthy has already been confirmed by the caller,
// so falsy values fall through to the single shared `return value` at the end.
const TRUTHY_VALUE_FORMATTERS: Partial<Record<BasicColumnTypes, (value, reportText: IReportLocale) => string>> = {
  [BasicColumnTypes.Currency]: (value, reportText) => reportText.reportTable.usdFormat({ value: parseFloat(value) }),
  [BasicColumnTypes.Numeric]: (value, reportText) => formatNumericValue(value, reportText.reportTable.numberFormat),
  [BasicColumnTypes.Percent]: (value, reportText) => reportText.reportTable.percentFormat({ value: parseFloat(value) }),
  [BasicColumnTypes.Period]: (value, reportText) => reportText.reportTable.periodFormat({ value: parseFloat(value) })
};

export const formatValueFromColumnType = (value, columnRecord, reportText: IReportLocale): string => {
  if (!columnRecord) return typeof value === 'string' ? value : '';

  const { type: columnType, semanticTypes } = columnRecord;
  if (semanticTypes?.includes(SemanticTypes.Calendar.GregorianYear)) {
    return value;
  }

  if (columnType === BasicColumnTypes.Date) {
    return formatDateValue(value, reportText.reportTable.dateFormat);
  }

  if (columnType === BasicColumnTypes.Boolean) {
    return formatBooleanValue(value, reportText);
  }

  const isValueTruthy = !isNaN(value) && value !== '' && value !== null;
  const truthyFormatter = isValueTruthy ? TRUTHY_VALUE_FORMATTERS[columnType as BasicColumnTypes] : undefined;

  return truthyFormatter ? truthyFormatter(value, reportText) : value;
};

export const getFormattedText = (
  value,
  columnName: string,
  detailTableColumns: IReportColumn[],
  reportText: IReportLocale
): string => {
  if (!detailTableColumns) return value;

  const columnRecord = getColumnByNameFromList(columnName, detailTableColumns);
  if (!columnRecord) return value;

  return formatValueFromColumnType(value, columnRecord, reportText);
};

export const getTooltipText = (
  operator: string,
  pillText: string,
  columnName: string,
  detailTableColumns: IReportColumn[],
  reportText: IReportLocale
): string => {
  if (!detailTableColumns) return pillText;
  if (!columnName) return pillText;

  const columnRecord = getColumnByNameFromList(columnName, detailTableColumns);
  if (!columnRecord) return pillText;

  const columnDisplayName = columnRecord.displayName;
  if (operator === FilterOperators.Blank || operator === FilterOperators.NotBlank) {
    return `${reportText.reportExplorer.filters[operator]} | ${columnDisplayName}`;
  }
  return `${reportText.reportExplorer.filters[operator]} ${pillText} | ${columnDisplayName}`;
};

export const getPillText = ({
  detailTableColumns,
  reportText,
  filter
}: {
  detailTableColumns;
  reportText;
  filter: IColumnFilter;
}): string => {
  const { columnName, operator, value } = filter;

  switch (operator) {
    case FilterOperators.Blank:
      return reportText.reportExplorer.filters.pillBlank;
    case FilterOperators.NotBlank:
      return reportText.reportExplorer.filters.pillNotBlank;
    default:
      return getFormattedText(value, columnName, detailTableColumns, reportText);
  }
};

const isDate = (columnType: BasicColumnTypes): boolean =>
  columnType === BasicColumnTypes.Date ||
  columnType === BasicColumnTypes.Year ||
  columnType === BasicColumnTypes.Period;

export const formatFiltersForPdfExport = ({
  reportText,
  isDimensional,
  columns,
  nonDimensionalFilters,
  dimensionalGlobalFilters,
  booleanOperators,
  columnsColumnDisplayName
}: {
  reportText: IReportLocale;
  isDimensional: boolean;
  columns: IReportColumn[];
  nonDimensionalFilters: IColumnFilter[];
  dimensionalGlobalFilters: ISegmentFilter[];
  booleanOperators: IColumnFilterBooleanOperator[];
  columnsColumnDisplayName: string;
}): string[] => {
  let filters = {};

  if (!isDimensional && nonDimensionalFilters?.length > 0) {
    filters = nonDimensionalFilters.reduce((acc, filter) => {
      const column = getColumnByNameFromList(filter.columnName, columns) as IReportColumn | undefined;
      if (!column) {
        console.warn('Skipping PDF export filter, column not found:', filter.columnName);
        return acc;
      }

      const valueText = formatValueFromColumnType(filter.value, column, reportText);
      let operatorText = '';

      const wrapQuotes = (columnType, valueText) =>
        columnType === BasicColumnTypes.Text ? `"${valueText}"` : valueText;

      if (column.type === BasicColumnTypes.Text) {
        operatorText = reportText.exportPdfModal.filters.text[filter.operator];
      } else if (
        column.type === BasicColumnTypes.Numeric ||
        column.type === BasicColumnTypes.Currency ||
        column.type === BasicColumnTypes.Percent
      ) {
        operatorText = reportText.exportPdfModal.filters.number[filter.operator];
      } else if (isDate(column.type)) {
        operatorText = reportText.exportPdfModal.filters.date[filter.operator];
      }
      const operator =
        booleanOperators.find((operator) => operator.columnName === column.name) ||
        booleanOperators.find((operator) => operator.columnName === getColumnName(column.name)) ||
        booleanOperators.find((operator) => getColumnName(operator.columnName) === column.name);
      const prefix = acc[column.displayName]
        ? `${acc[column.displayName]}, ${operator ? operator.booleanOperator : 'and'} `
        : '';

      const valueTextWrapQuotes = valueText && `${wrapQuotes(column.type, valueText)}`;
      acc[column.displayName] = `${prefix}${operatorText} ${valueTextWrapQuotes}`;

      return acc;
    }, {});
  } else {
    filters = (nonDimensionalFilters || []).reduce((acc, filter) => {
      // Account summary filters are all 'and' filters
      const operator = acc[columnsColumnDisplayName] ? 'and' : '';
      const prefix = acc[columnsColumnDisplayName] ? `${acc[columnsColumnDisplayName]}, ${operator} ` : '';
      acc[columnsColumnDisplayName] = `${prefix}${reportText.exportPdfModal.filters.date[filter.operator]} ${
        filter.value
      }`;
      return acc;
    }, {});

    filters = (dimensionalGlobalFilters || []).reduce((acc, filter) => {
      acc[filter.label] = `: ${filter.value}`;
      return acc;
    }, filters);
  }

  return Object.keys(filters).map((name) => `${name} ${filters[name]}`);
};
